import CryptoJS from "crypto-js";
import axios from "axios";

const API_URL =
  requireEnv(process.env.REACT_APP_STUDIO_API_URL, "REACT_APP_STUDIO_API_URL");
const FRONTEND_API_URL = requireEnv(
  process.env.REACT_APP_API_BASE_URL,
  "REACT_APP_API_BASE_URL",
);
const USERS_AUTH_PATH =
  requireEnv(
    process.env.REACT_APP_STUDIO_USERS_AUTH_PATH,
    "REACT_APP_STUDIO_USERS_AUTH_PATH",
  );
const STUDIO_AUTH_PATH =
  requireEnv(
    process.env.REACT_APP_STUDIO_AUTH_PATH,
    "REACT_APP_STUDIO_AUTH_PATH",
  );
const RAW_STUDIO_URL =
  requireEnv(process.env.REACT_APP_STUDIO_URL, "REACT_APP_STUDIO_URL");
const COOKIE_ID = requireEnv(
  process.env.REACT_APP_STUDIO_COOKIE_ID,
  "REACT_APP_STUDIO_COOKIE_ID",
);
const COOKIE_DOMAIN =
  process.env.REACT_APP_STUDIO_COOKIE_DOMAIN;
const LOCAL_FRONTEND_HOSTS = (
  requireEnv(
    process.env.REACT_APP_LOCAL_FRONTEND_HOSTS,
    "REACT_APP_LOCAL_FRONTEND_HOSTS",
  )
)
  .split(",")
  .map((host) => host.trim())
  .filter(Boolean);
const ENCRYPTION_KEY =
  requireEnv(
    process.env.REACT_APP_STUDIO_ENCRYPTION_KEY,
    "REACT_APP_STUDIO_ENCRYPTION_KEY",
  );
const PARTNER_SLUG =
  requireEnv(
    process.env.REACT_APP_STUDIO_PARTNER_SLUG,
    "REACT_APP_STUDIO_PARTNER_SLUG",
  );

function requireEnv(value: string | undefined, key: string) {
  if (value === undefined) {
    throw new Error(`${key} is required in .env`);
  }

  return value;
}

type LoginType = "email" | "phone";

export interface StudioLoginResponse {
  statusCode: number;
  error: boolean;
  message?: string;
  response?: StudioUser;
}

export interface StudioUser {
  access_token?: string;
  refresh_token?: string;
  is_cheif?: number | boolean;
  is_chief?: number | boolean;
  role_id?: number;
  studios?: Array<{ slug?: string }>;
  partner?: {
    is_partner?: number | boolean;
  };
  [key: string]: unknown;
}

export interface StudioSession {
  access_token?: string;
  refresh_token?: string;
}

export function getLoginType(phoneEmail: string): LoginType {
  return /^\+?\d{6,15}$/.test(phoneEmail.trim()) ? "phone" : "email";
}

export function isValidAdminIdentifier(phoneEmail: string) {
  const value = phoneEmail.trim();

  if (getLoginType(value) === "phone") {
    return /^\+?\d{6,15}$/.test(value);
  }

  return /^[_A-Za-z0-9-+]+(\.[_A-Za-z0-9-]+)*@[A-Za-z0-9-]+(\.[A-Za-z0-9]+)*(\.[A-Za-z]{2,})/.test(
    value,
  );
}

export async function loginToStudio(
  phoneEmail: string,
  password: string,
): Promise<StudioLoginResponse> {
  const type = getLoginType(phoneEmail);
  const payload = {
    login_type: "normal",
    acesstype: "web",
    phoneEmail,
    password,
    social_user_id: "",
    token: "",
    profile_picture: "",
    type,
    first_name: "",
    is_email_verified: "",
    browsername: getBrowserName(),
    deviceDetail: `web - ${navigator.platform}`,
    user: null,
  };

  let data = await postLoginWithBackendFallback(payload);

  if (shouldTryStudioAuth(data)) {
    data = await postLogin(`${API_URL}${STUDIO_AUTH_PATH}login`, payload);
  }

  if (data.statusCode === 200 && data.error === false && data.response) {
    persistStudioSession(data.response);
  }

  return data;
}

async function postLoginWithBackendFallback(payload: Record<string, unknown>) {
  if (FRONTEND_API_URL) {
    const data = await postLogin(
      `${normalizeBaseUrl(FRONTEND_API_URL)}auth/studio-login`,
      payload,
    );

    if (!shouldFallbackToDirectStudioLogin(data)) {
      return data;
    }
  }

  return postLogin(`${API_URL}${USERS_AUTH_PATH}login`, payload);
}

async function postLogin(url: string, payload: Record<string, unknown>) {
  try {
    const { data } = await axios.post<StudioLoginResponse>(url, payload, {
      headers: {
        Accept: "application/json",
        "X-REQUEST-TYPE": "web",
      },
    });

    return data;
  } catch (error: any) {
    if (error?.response?.data) {
      return error.response.data as StudioLoginResponse;
    }

    throw error;
  }
}

function shouldFallbackToDirectStudioLogin(data: StudioLoginResponse | any) {
  const message = String(data?.message || "").toLowerCase();

  return (
    data?.statusCode === 404 ||
    message.includes("route not found") ||
    message.includes("cannot post")
  );
}

function shouldTryStudioAuth(data: StudioLoginResponse) {
  if (data.statusCode === 200 && data.error === false) {
    return false;
  }

  return data.statusCode === 401 || data.statusCode === 422;
}

export function getStudioRedirectUrl(user: StudioUser) {
  const studioSlug = user.studios?.[0]?.slug;
  const isPartner = Number(user.partner?.is_partner) === 1;
  const roleId = Number(user.role_id);

  if (
    user.is_cheif ||
    user.is_chief ||
    (studioSlug && !isPartner && roleId !== 2 && roleId !== 3) ||
    (studioSlug && roleId === 4)
  ) {
    return buildStudioUrl(`${studioSlug}/dashboard?getStart=1`);
  }

  if (studioSlug && roleId === 2) {
    return buildStudioUrl(`${studioSlug}/super-admin/studio-partner?getStart=1`);
  }

  if (studioSlug && roleId === 3) {
    return buildStudioUrl(`${studioSlug}/super-admin/transac-management?getStart=1`);
  }

  if (isPartner) {
    return buildStudioUrl(`${PARTNER_SLUG}/dashboard?getStart=1`);
  }

  return "/";
}

export function canShowStudioConsole(user?: StudioUser | null) {
  if (!user) {
    return false;
  }

  const roleSlug = getStudioRoleSlug(user);
  const hasActiveStudioUser = hasActiveStudioAccess(user);

  return Boolean(
    roleSlug === "partner" ||
      roleSlug === "sub-admin" ||
      roleSlug === "monetization-manager" ||
      roleSlug === "media-manager" ||
      roleSlug === "partner-manager" ||
      roleSlug === "super-admin" ||
      roleSlug === "admin" ||
      user.is_cheif ||
      user.is_chief ||
      user.is_admin ||
      user.is_superadmin ||
      user.is_super_admin ||
      Number(user.partner?.is_partner) === 1 ||
      hasActiveStudioUser ||
      getStudioSlug(user),
  );
}

export function getStudioConsoleButtonLabel(user?: StudioUser | null) {
  const roleSlug = user ? getStudioRoleSlug(user) : "";

  if (
    roleSlug === "partner" ||
    roleSlug === "monetization-manager" ||
    roleSlug === "media-manager" ||
    roleSlug === "partner-manager"
  ) {
    return "Go to Admin Dashboard";
  }

  return "Admin Console";
}

export function getStudioConsoleUrl(user: StudioUser) {
  const studioSlug = getStudioSlug(user);
  const roleId = Number(user.role_id);
  const roleSlug = getStudioRoleSlug(user);
  const isPartner = Number(user.partner?.is_partner) === 1;
  const hasStudio = Boolean(studioSlug);

  if ((roleSlug === "partner" || roleSlug === "partner-manager") && studioSlug) {
    return buildStudioUrl(`${studioSlug}/insights`);
  }

  if (
    user.is_cheif ||
    user.is_chief ||
    user.is_admin ||
    user.is_superadmin ||
    user.is_super_admin ||
    roleSlug === "super-admin" ||
    roleSlug === "admin"
  ) {
    return buildStudioUrl("ekluvya/dashboard");
  }

  if (isPartner && !hasStudio) {
    return buildStudioUrl(`${PARTNER_SLUG}/dashboard`);
  }

  if (hasStudio && (roleId === 2 || roleSlug === "sub-admin")) {
    return buildStudioUrl(`${studioSlug}/super-admin/studio-management`);
  }

  if (hasStudio && (roleId === 3 || roleSlug === "monetization-manager")) {
    return buildStudioUrl(`${studioSlug}/super-admin/analytics-management`);
  }

  if (hasStudio && (roleId === 4 || roleSlug === "media-manager")) {
    return buildStudioUrl(`${studioSlug}/super-admin/studio-management`);
  }

  if (hasStudio) {
    return buildStudioUrl(`${studioSlug}/dashboard`);
  }

  return buildStudioUrl("create-studio");
}

export function getStudioSessionFromCookie(): StudioSession | null {
  const cookieValue = getCookie(COOKIE_ID);

  if (!cookieValue) {
    return null;
  }

  return decrypt<StudioSession>(cookieValue);
}

export function clearStudioSession() {
  localStorage.removeItem("gudUser");
  deleteCookie(COOKIE_ID);
}

export function setStudioAccessTokenSession(
  accessToken: string,
  refreshToken = "",
) {
  if (!accessToken) {
    return;
  }

  persistStudioTokenSession({
    access_token: accessToken,
    refresh_token: refreshToken,
  });
}

export function setStudioUserSession(user: StudioUser) {
  persistStudioSession(user);
}

export async function fetchStudioUserProfile(accessToken: string) {
  try {
    const { data } = await axios.get(`${API_URL}${USERS_AUTH_PATH}profile`, {
      headers: {
        Accept: "application/json",
        Authorization: `Bearer ${accessToken}`,
        "X-REQUEST-TYPE": "web",
      },
    });

    return data?.response || data?.user || data?.data?.user || data?.data || null;
  } catch (error: any) {
    if (error?.response?.status === 401) {
      const refreshedSession = await refreshStudioSession();

      if (refreshedSession?.access_token) {
        return fetchStudioUserProfileWithoutRefresh(refreshedSession.access_token);
      }

      clearStudioSession();
    }

    return null;
  }
}

async function fetchStudioUserProfileWithoutRefresh(accessToken: string) {
  try {
    const { data } = await axios.get(`${API_URL}${USERS_AUTH_PATH}profile`, {
      headers: {
        Accept: "application/json",
        Authorization: `Bearer ${accessToken}`,
        "X-REQUEST-TYPE": "web",
      },
    });

    return data?.response || data?.user || data?.data?.user || data?.data || null;
  } catch {
    return null;
  }
}

async function refreshStudioSession(): Promise<StudioSession | null> {
  const currentSession = getStudioSessionFromCookie();

  if (!currentSession?.refresh_token) {
    return null;
  }

  try {
    const { data } = await axios.post(
      `${API_URL}${USERS_AUTH_PATH}refresh-token`,
      {
        refresh_token: currentSession.refresh_token,
      },
      {
        headers: {
          Accept: "application/json",
          Authorization: currentSession.access_token
            ? `Bearer ${currentSession.access_token}`
            : "",
          "X-REQUEST-TYPE": "web",
        },
      },
    );

    const refreshedSession = {
      access_token: data?.response?.access_token,
      refresh_token:
        data?.response?.refresh_token || currentSession.refresh_token,
    };

    if (!refreshedSession.access_token) {
      return null;
    }

    persistStudioTokenSession(refreshedSession);
    sessionStorage.setItem("token", refreshedSession.access_token);
    sessionStorage.setItem("refreshToken", refreshedSession.refresh_token || "");

    return refreshedSession;
  } catch {
    return null;
  }
}

function getStudioRoleSlug(user: StudioUser) {
  const roleUser = user.role_user as any;
  const rolesUser = user.roles_user as any;
  const roleName =
    user.role_slug ||
    roleUser?.role_name ||
    roleUser?.user_role_name ||
    rolesUser?.[0]?.role_id?.role_name ||
    rolesUser?.[0]?.role_name ||
    user.role_name ||
    user.roleName ||
    "";
  return (
    user.role_slug ||
    roleUser?.user_role_slug ||
    roleUser?.role_slug ||
    rolesUser?.[0]?.role_id?.slug ||
    rolesUser?.[0]?.role_id?.role_slug ||
    rolesUser?.[0]?.slug ||
    String(roleName).trim().toLowerCase().replace(/\s+/g, "-") ||
    ""
  );
}

function getStudioSlug(user: StudioUser) {
  const studios = user.studios as any;
  const studioUser = user.studio_user as any;
  const roleUser = user.role_user as any;

  return (
    user.studio_slug ||
    user.studioSlug ||
    user.channel_slug ||
    user.channelSlug ||
    studios?.[0]?.slug ||
    studios?.[0]?.studio_slug ||
    studioUser?.[0]?.studio_id?.slug ||
    studioUser?.[0]?.studio?.slug ||
    studioUser?.[0]?.studio_slug ||
    studioUser?.[0]?.slug ||
    roleUser?.studios?.[0]?.slug ||
    roleUser?.studios?.[0]?.studio_slug ||
    ""
  ) as string;
}

function hasActiveStudioAccess(user: StudioUser) {
  const studioUser = user.studio_user as any;

  if (!Array.isArray(studioUser)) {
    return false;
  }

  return studioUser.some((item) => item?.is_active !== false);
}

function getStudioBaseUrl() {
  return normalizeBaseUrl(RAW_STUDIO_URL);
}

function buildStudioUrl(path: string) {
  return `${getStudioBaseUrl()}${path.replace(/^\/+/, "")}`;
}

function persistStudioSession(user: StudioUser) {
  const tokenCookie = encryptStudioSession({
    access_token: user.access_token,
    refresh_token: user.refresh_token,
  });
  const userData = encrypt(user);

  localStorage.setItem("gudUser", userData);
  setCookie(COOKIE_ID, user.access_token ? tokenCookie : userData);
}

function persistStudioTokenSession(session: StudioSession) {
  setCookie(COOKIE_ID, encryptStudioSession(session));
}

function encryptStudioSession(session: StudioSession) {
  return encrypt({
    access_token: session.access_token,
    refresh_token: session.refresh_token,
  });
}

function encrypt(value: unknown) {
  return CryptoJS.AES.encrypt(JSON.stringify(value), ENCRYPTION_KEY).toString();
}

function decrypt<T>(value: string): T | null {
  try {
    const bytes = CryptoJS.AES.decrypt(value, ENCRYPTION_KEY);
    const decrypted = bytes.toString(CryptoJS.enc.Utf8);

    if (!decrypted) {
      return null;
    }

    return JSON.parse(decrypted) as T;
  } catch {
    return null;
  }
}

function setCookie(name: string, value: string) {
  const expires = new Date(Date.now() + 365 * 86400000).toUTCString();
  const encodedValue = encodeURIComponent(value);
  const cookieAttributes = getCookieAttributes();

  document.cookie = `${name}=${encodedValue}; expires=${expires}; ${cookieAttributes}`;
}

function getCookie(name: string) {
  const cookies = document.cookie ? document.cookie.split("; ") : [];
  const target = `${name}=`;
  const match = cookies.find((cookie) => cookie.startsWith(target));

  return match ? decodeURIComponent(match.slice(target.length)) : "";
}

function deleteCookie(name: string) {
  const cookieAttributes = getCookieAttributes();

  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; ${cookieAttributes}`;
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; SameSite=Lax`;
}

function getCookieAttributes() {
  const domain = resolveCookieDomain();
  const attributes = ["path=/", "SameSite=Lax"];

  if (domain) {
    attributes.splice(1, 0, `domain=${domain}`);
  }

  return attributes.join("; ");
}

function resolveCookieDomain() {
  const configuredDomain = (COOKIE_DOMAIN || "").trim();

  if (!configuredDomain || configuredDomain === "?" || isConfiguredLocalHost()) {
    return "";
  }

  return configuredDomain;
}

function isConfiguredLocalHost() {
  if (typeof window === "undefined") {
    return false;
  }

  return LOCAL_FRONTEND_HOSTS.includes(window.location.hostname);
}

function normalizeBaseUrl(url: string) {
  return url.endsWith("/") ? url : `${url}/`;
}

function getBrowserName() {
  const ua = navigator.userAgent;

  if (ua.includes("Edg/")) return "Edge";
  if (ua.includes("Chrome/")) return "Chrome";
  if (ua.includes("Firefox/")) return "Firefox";
  if (ua.includes("Safari/")) return "Safari";

  return "Web";
}
